import React, { useEffect, useState } from 'react';

import debounce from 'lodash/debounce';

import { Label } from '@atlaskit/form';
import { AsyncSelect } from '@atlaskit/select';

import { DEBOUNCE_DELAY_MS } from '@qality/common-config';
import { messages } from '@qality/common-messages';
import { TestCycleOption } from '@qality/common-model/src/TestCycle';
import { StorageOfUniqueServices, UserService } from '@qality/common-service';

import UserImpl from '../../../select/UserTestImpl';
import { PAGE_NUMBER } from '../ExecutionDialog';
import TestCaseForExecutionSelectOption from './TestCaseForExecutionSelectOption';
import {
  fetchTestCaseInCycleForExecutions,
  getUserAccountIdsFromOptions,
  initializeAvatarUrlsForUsers,
} from './TestCaseForExecutionService';

type Props = {
  selectedOption: TestCycleOption;
  setSelectedOption: (option: TestCycleOption) => void;
  testCaseId: string;
  isExecutionDisabled: boolean;
  onClose: () => void;
};

export default function TestCaseForExecutionSelect({
  selectedOption,
  setSelectedOption,
  testCaseId,
  isExecutionDisabled,
  onClose,
}: Props): JSX.Element {
  const [isLoading, setIsLoading] = useState(true);
  const [options, setOptions] = useState<Array<TestCycleOption>>([]);
  const [cachedUsers, setCachedUsers] = useState<Record<string, UserImpl>>({});

  useEffect(() => {
    initializeOptions();
  }, [setOptions, onClose, testCaseId]);

  const initializeOptions = async () => {
    const newOptions: Array<TestCycleOption> =
      await fetchTestCaseInCycleForExecutions(PAGE_NUMBER, testCaseId);

    const optionsWithUsers = await addUserDataToOptions(newOptions);
    setOptions(optionsWithUsers);
    setIsLoading(false);
  };

  const loadOptions = async (
    text: string,
    callback: (newOptions: TestCycleOption[]) => void
  ) => {
    setIsLoading(true);
    const newOptions: Array<TestCycleOption> =
      await fetchTestCaseInCycleForExecutions(PAGE_NUMBER, testCaseId, text);

    const optionsWithUsers = await addUserDataToOptions(newOptions);
    setOptions(optionsWithUsers);
    callback(optionsWithUsers);
    setIsLoading(false);
  };

  const addUserDataToOptions = async (
    optionsArg: Array<TestCycleOption>
  ): Promise<Array<TestCycleOption>> => {
    const missingUserIds = getUserAccountIdsFromOptions(optionsArg).filter(
      (id) => !(id in cachedUsers)
    );

    const fetchedUsers = await fetchMissingUsers(missingUserIds);
    const usersMap = {
      ...cachedUsers,
      ...Object.fromEntries(fetchedUsers.map((u) => [u.accountId, u])),
    };

    return initializeAvatarUrlsForUsers(optionsArg, usersMap);
  };

  const fetchMissingUsers = async (
    missingUserIds: Array<string>
  ): Promise<Array<UserImpl>> => {
    try {
      let fetchedUsers: UserImpl[] = [];
      if (missingUserIds.length > 0) {
        fetchedUsers = await UserService.fetchAssignees(missingUserIds);
        setCachedUsers((prev) => ({
          ...prev,
          ...Object.fromEntries(
            fetchedUsers.map((user) => [user.accountId, user])
          ),
        }));
      }
      return fetchedUsers;
    } catch (e) {
      StorageOfUniqueServices.get('ErrorHandlingUtils').handleError(
        e,
        messages['error.testCases.fetchingUsersForTestExecutions.message']
      );
      return [];
    }
  };

  const loadOptionsDebounced = debounce(loadOptions, DEBOUNCE_DELAY_MS);

  const handleChange = (e: TestCycleOption) => {
    if (!options.some((option) => option.value === e.value)) {
      setOptions([...options, e]);
    }
    setSelectedOption(e);
  };

  const getFormatOptionLabel = (option, { context }) => {
    return (
      <TestCaseForExecutionSelectOption
        option={option}
        isSelectedOption={context !== 'menu'}
      />
    );
  };

  return (
    <>
      <Label htmlFor='test-cycle-for-execution-select-label'>Execute</Label>
      <AsyncSelect
        placeholder='Waiting for data...'
        value={selectedOption}
        defaultOptions={options}
        loadOptions={loadOptionsDebounced}
        isLoading={isLoading}
        isDisabled={isExecutionDisabled}
        onChange={handleChange}
        maxMenuHeight={300}
        formatOptionLabel={getFormatOptionLabel}
        styles={{
          container: (provided) => ({
            ...provided,
            width: '552px',
          }),
        }}
      />
    </>
  );
}
